import { uniqBy } from 'lodash'
import { ReactNode, useMemo } from 'react'
import { twMerge as twm } from 'tailwind-merge'

import Button from '../../app/design/Button'
import Heading from '../../app/design/Heading'
import Tag, { TagProps } from '../../app/design/Tag'
import { Select } from '../fields/Select'
import { IconField } from './IconField'
import { LocationSection } from './LocationSection'
import { NotesSection } from './NotesSection'
import { PaymentsSection } from './PaymentsSection'

export type HeaderButton = {
    title: string
    onClick: () => void
    intent?: 'primary' | 'secondary' | 'secondaryDestructive'
    longTitle?: string
}

type Props = {
    title: string
    backButton?: {
        title: string
        onClick: () => void
    }
    tag?: {
        label: string
        intent?: TagProps['intent']
    }
    headerButtons?: HeaderButton[]
    children?: ReactNode
}

export const BaseRecordView = ({ title, backButton, tag, headerButtons = [], children }: Props) => {
    // We want to show a max of 4 buttons, with an optional fifth if there's a primary button. If
    // there are more than 4 buttons, we show a dropdown menu with the overflow.
    const [visibleHeaderButtons, overflowHeaderButtons, primaryHeaderButton] = useMemo(() => {
        const buttons = uniqBy(headerButtons, (button) => button.title)
        const primaryButton = buttons.find((button) => button.intent === 'primary')
        const secondaryButtons = buttons.filter((button) => button.title !== primaryButton?.title)

        return [
            secondaryButtons.length > 4 ? secondaryButtons.slice(0, 3) : secondaryButtons,
            secondaryButtons.length > 4 ? secondaryButtons.slice(3) : [],
            primaryButton,
        ]
    }, [headerButtons])

    return (
        <div className="flex max-h-screen items-start justify-center overflow-y-auto px-2">
            <div className="max-w-xlarge w-full pt-4 pb-6">
                {backButton !== undefined && (
                    <div className="mb-3 h-6 px-6">
                        <Button
                            intent="tertiaryLink"
                            leftIcon="chevronLeft.medium"
                            className="px-0"
                            isDense
                            onClick={backButton.onClick}
                        >
                            {backButton.title}
                        </Button>
                    </div>
                )}

                <div className="flex items-center justify-start gap-x-4 pr-8 pl-6">
                    <Heading size="md">{title}</Heading>

                    {tag !== undefined && (
                        <Tag
                            label={tag.label}
                            intent={tag.intent}
                            className="border-2 border-white"
                        />
                    )}

                    {headerButtons.length > 0 && (
                        <div className="flex grow items-center justify-end gap-x-2">
                            {visibleHeaderButtons.map((buttonConfig) => (
                                <Button
                                    key={buttonConfig.title}
                                    intent={buttonConfig.intent ?? 'secondary'}
                                    size="small"
                                    onClick={buttonConfig.onClick}
                                >
                                    {buttonConfig.title}
                                </Button>
                            ))}

                            {overflowHeaderButtons.length > 0 && (
                                <Select
                                    trigger={
                                        <Button
                                            intent="secondary"
                                            size="small"
                                            rightIcon="chevronDown.small"
                                        >
                                            More options
                                        </Button>
                                    }
                                    options={overflowHeaderButtons.map(
                                        ({ title, longTitle, onClick }) => ({
                                            id: title,
                                            title: longTitle ?? title,
                                            onClick,
                                        })
                                    )}
                                    onSelectOption={({ id }) => {
                                        const menuButtonConfig = overflowHeaderButtons.find(
                                            (button) => button.title === id
                                        )
                                        if (!menuButtonConfig) {
                                            return
                                        }
                                        menuButtonConfig.onClick()
                                    }}
                                    align="end"
                                />
                            )}

                            {primaryHeaderButton && (
                                <Button
                                    intent="primary"
                                    size="small"
                                    onClick={primaryHeaderButton.onClick}
                                >
                                    {primaryHeaderButton.title}
                                </Button>
                            )}
                        </div>
                    )}
                </div>

                <div className="mt-6">{children}</div>
            </div>
        </div>
    )
}

const BaseRecordViewContainer = ({
    children,
    className,
}: {
    children: ReactNode
    className?: string
}) => {
    return (
        <div
            className={twm('mt-6 grid w-full grid-cols-[360px_minmax(0,744px)] gap-x-6', className)}
        >
            {children}
        </div>
    )
}

const BaseRecordViewLeftColumn = ({
    children,
    className,
}: {
    children: ReactNode
    className?: string
}) => {
    return (
        <div className={twm('flex grow flex-col items-stretch justify-start gap-y-6', className)}>
            {children}
        </div>
    )
}

const BaseRecordViewRightColumn = ({
    children,
    className,
}: {
    children: ReactNode
    className?: string
}) => {
    return (
        <div className={twm('flex grow flex-col items-stretch justify-start gap-y-6', className)}>
            {children}
        </div>
    )
}

BaseRecordView.IconField = IconField
BaseRecordView.LocationSection = LocationSection
BaseRecordView.NotesSection = NotesSection
BaseRecordView.PaymentsSection = PaymentsSection
BaseRecordView.Container = BaseRecordViewContainer
BaseRecordView.LeftColumn = BaseRecordViewLeftColumn
BaseRecordView.RightColumn = BaseRecordViewRightColumn
